import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import { GuideTabs } from '../../../theme/components/GuideTabs';
import { ProviderServiceMappingsSection } from './ProviderServiceMappings.Section';
import { ProviderReferenceTemplatesSection, ProviderReferenceTemplatesSectionProps } from './ProviderReferenceTemplates.Section';
import {
  ProviderMedicalServiceMapping,
  ServiceMappingsPage,
  ServiceMappingsQuery,
} from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';

/** Solapas de la configuración: "Formatos de referencia" (US5) y "Correspondencias de servicios" (US6). */
export type ImportSettingsTab = 'referenceTemplates' | 'serviceMappings';

export interface ImportSettingsDrawerProps {
  open: boolean;
  onDismiss: () => void;
  providerName: string;
  /** Solapa con que abre (por defecto, la primera disponible). */
  initialTab?: ImportSettingsTab;
  /** Formatos de referencia (US5); sin ellos no se muestra la solapa. */
  templates?: ProviderReferenceTemplatesSectionProps;
  /** Correspondencias de servicios (US6); sin ellas no se muestra la solapa. */
  LoadServiceMappings?: (filter: ServiceMappingsQuery) => Promise<ServiceMappingsPage>;
  UpdateServiceMapping?: (mappingId: string, medicalServiceId: number) => Promise<ProviderMedicalServiceMapping | undefined>;
  DeleteServiceMapping?: (mappingId: string) => Promise<void>;
}

/**
 * "Configuración de importación" del proveedor (spec 009, DEC-APP-001): `GuideDrawer` large con `GuideTabs`
 * "Formatos de referencia" (US5) y "Correspondencias de servicios" (US6), abierto desde la pestaña de consumos sin tocar
 * la cadena flex de la grilla (008). Cada solapa aparece sólo si llegan sus callbacks.
 */
export const ImportSettingsDrawer = (props: ImportSettingsDrawerProps) => {
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.importSettings.${key}`, options) as string;
  const hasMappings = !!props.LoadServiceMappings && !!props.UpdateServiceMapping && !!props.DeleteServiceMapping;
  const firstTab: ImportSettingsTab = props.templates ? 'referenceTemplates' : 'serviceMappings';
  const [tab, setTab] = useState<string>(props.initialTab ?? firstTab);

  useEffect(() => {
    if (props.open) setTab(props.initialTab ?? firstTab);
  }, [props.open, props.initialTab, firstTab]);

  return (
    <GuideDrawer
      open={props.open}
      size="large"
      onDismiss={props.onDismiss}
      closeLabel={tr('close')}
      eyebrow={tr('eyebrow', { name: props.providerName })}
      title={tr('title')}
      subtitle={tr('subtitle')}
      testId="pcs-import-settings-drawer"
      secondaryAction={{ label: tr('close') }}
    >
      {props.open && (
        <GuideTabs
          ariaLabel={tr('tabsLabel')}
          selectedKey={tab}
          onTabChange={setTab}
          data-testid="pcs-import-settings-tabs"
          tabs={[
            props.templates && {
              key: 'referenceTemplates',
              label: tr('tabs.referenceTemplates'),
              content: <ProviderReferenceTemplatesSection {...props.templates} />,
            },
            hasMappings && {
              key: 'serviceMappings',
              label: tr('tabs.serviceMappings'),
              content: (
                <ProviderServiceMappingsSection
                  LoadMappings={props.LoadServiceMappings!}
                  UpdateMapping={props.UpdateServiceMapping!}
                  DeleteMapping={props.DeleteServiceMapping!}
                />
              ),
            },
          ]}
        />
      )}
    </GuideDrawer>
  );
};

export default ImportSettingsDrawer;
